import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Megaphone, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { InviteBlastCard } from "../../components/guest/InviteBlastCard";
import { StoryRepliesCard } from "../../components/social/StoryRepliesCard";

const card = "bg-white border border-[var(--line)] rounded-2xl p-5";

function Thread({ wid, guest, onRead }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/guest-inbox/${wid}/${guest.guest_id}`).then((r) => { setMsgs(r.data); onRead(); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [guest.guest_id]); // eslint-disable-line
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);
  async function send(e) {
    e.preventDefault();
    try { await api.post(`/guest-inbox/${wid}/${guest.guest_id}`, { text }); setText(""); load(); } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className="flex flex-col h-full" data-testid="inbox-thread">
      <p className="font-serif text-2xl mb-3">{guest.name}</p>
      <div className="flex-1 min-h-[300px] max-h-[420px] overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.map((m) => (
          <div key={m.msg_id} className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender === "couple" ? "ml-auto bg-[#111] text-white" : "bg-[var(--bg-warm)]"}`} data-testid={`inbox-msg-${m.msg_id}`}>
            <p className={`text-[10px] ${m.sender === "couple" ? "text-[#E7CF9A]" : "text-[var(--gold)]"}`}>{m.author}</p><p className="whitespace-pre-line">{m.text}</p>
          </div>
        ))}
        {!msgs.length && <p className="text-sm text-[var(--muted)]">Start the conversation.</p>}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input required value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder={`Reply to ${guest.name}…`} className="input-luxe flex-1" data-testid="inbox-reply-input" />
        <button className="btn-gold text-xs" data-testid="inbox-reply-send"><Send className="w-3 h-3" /> Send</button>
      </form>
    </div>
  );
}

function Announce({ wid, rows, reload }) {
  const [f, setF] = useState({ title: "", text: "", notify: true });
  async function post(e) {
    e.preventDefault();
    try { const { data } = await api.post(`/guest-inbox/${wid}/announcements`, f); toast.success(`Posted${f.notify ? ` and sent to ${data.notified} guest${data.notified === 1 ? "" : "s"}` : ""}`); setF({ title: "", text: "", notify: true }); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className={card} data-testid="inbox-announcements">
      <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[var(--gold)]" /> Announcements</h3>
      <form onSubmit={post} className="space-y-2 mb-4">
        <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={120} placeholder="Title, e.g. Shuttle times" className="input-luxe" data-testid="ann-title" />
        <textarea required value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} maxLength={2000} rows={3} placeholder="What should every guest know?" className="input-luxe resize-none" data-testid="ann-text" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.notify} onChange={(e) => setF({ ...f, notify: e.target.checked })} data-testid="ann-notify" /> Also email/text guests who have a portal login</label>
        <button className="btn-gold text-xs" data-testid="ann-post">Post update</button>
      </form>
      {rows.map((a) => (
        <div key={a.ann_id} className="flex justify-between gap-3 py-2 border-t border-[var(--line)]/60 text-sm" data-testid={`ann-${a.ann_id}`}>
          <div><p className="font-medium">{a.title}</p><p className="text-[var(--muted)] whitespace-pre-line">{a.text}</p></div>
          <button onClick={async () => { if (!window.confirm("Delete this update for every guest?")) return; try { await api.delete(`/guest-inbox/${wid}/announcements/${a.ann_id}`); reload(); } catch (err) { toast.error(formatApiError(err)); } }} className="p-2 text-red-600 self-start" data-testid={`ann-del-${a.ann_id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function GuestMessagesPage() {
  const [sp] = useSearchParams();
  const [weds, setWeds] = useState(null);
  const [wid, setWid] = useState(sp.get("wedding") || "");
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/guest-inbox/weddings").then((r) => { setWeds(r.data); if (!wid && r.data[0]) setWid(r.data[0].wedding_id); }).catch((e) => toast.error(formatApiError(e))); }, []); // eslint-disable-line
  const load = () => wid && api.get(`/guest-inbox/${wid}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { setSel(null); load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [wid]); // eslint-disable-line
  if (!weds) return null;
  if (!weds.length) return <p className="text-[var(--muted)]" data-testid="inbox-none">Create your wedding first.</p>;
  const people = d ? [...d.threads, ...d.portal_guests.filter((p) => !d.threads.some((t) => t.guest_id === p.guest_id))] : [];
  return (
    <div className="space-y-5" data-testid="guest-messages-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Guest portal</p>
          <h1 className="font-serif font-light text-4xl">Guest Messages</h1>
          <p className="text-[var(--muted)] mt-2 text-sm">Private chats with guests who log in to their portal, plus updates for everyone.</p>
        </div>
        {weds.length > 1 && <select value={wid} onChange={(e) => setWid(e.target.value)} className="input-luxe !w-auto" data-testid="inbox-wedding-select">{weds.map((w) => <option key={w.wedding_id} value={w.wedding_id}>{w.couple_names}</option>)}</select>}
      </div>
      <StoryRepliesCard />
      {d && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          <div className={`${card} !p-2 max-h-[540px] overflow-y-auto`} data-testid="inbox-threads">
            {people.map((t) => (
              <button key={t.guest_id} onClick={() => setSel(t)} data-testid={`inbox-thread-${t.guest_id}`} className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${sel?.guest_id === t.guest_id ? "bg-[var(--bg-warm)]" : "hover:bg-black/[0.03]"}`}>
                <div className="flex justify-between"><span className="font-medium text-sm">{t.name}</span>{t.unread > 0 && <span className="text-[10px] bg-[var(--gold)] text-white rounded-full px-2">{t.unread}</span>}</div>
                <p className="text-xs text-[var(--muted)] truncate">{t.last || "No messages yet"}</p>
              </button>
            ))}
            {!people.length && <p className="text-sm text-[var(--muted)] p-3">No guests have logged in yet. Guests create a login from their personal RSVP link.</p>}
          </div>
          <div className={card}>{sel ? <Thread wid={wid} guest={sel} onRead={load} /> : <p className="text-sm text-[var(--muted)]">Pick a guest to read and reply.</p>}</div>
        </div>
      )}
      <InviteBlastCard />
      {d && <Announce wid={wid} rows={d.announcements} reload={load} />}
    </div>
  );
}
